import IProject from "common/interfaces/IProject";
import Popup from "components/Popup";
import { Dispatch, SetStateAction, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import themeProps from "theme/themes";
import reject from "assets/icons/close_square_red.svg";
import accept from "assets/icons/check_square_green.svg";
import { useAppDispatch } from "store/hooks";
import { assignUser, removeApplicant } from "store/slices/exploreSlice";
import { patch } from "core/Api";

interface ApplicantsPopupProps {
  project?: IProject;
  handleClose: Dispatch<SetStateAction<boolean>>;
}

const Wrapper = styled.div`
  margin-bottom: 20px;
`;

const ApplicantsWrapper = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 24px 20px;
`;
const RoleName = styled.h3`
  margin-bottom: 24px;
`;

const Applicant = styled.div`
  color: ${(props: themeProps) => props.theme.text.primary};
  padding: 8px 15px;
  width: fit-content;
  border: 1px solid ${(props: themeProps) => props.theme.border.secondary};
  border-radius: 8px;
  display: flex;
  align-items: center;
`;

const ApplicantPicture = styled.div`
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background-color: gray;
  margin-right: 8px;
`;

const Button = styled.button`
  background-size: cover;
  background-color: transparent;
  background-repeat: no-repeat;
  cursor: pointer;
  border: none;
  outline: none;
  border-radius: 12px;
  width: 25px;
  height: 25px;
  margin-left: 8px;
`;

const AcceptButton = styled(Button)`
  background-image: url(${accept});
`;
const RejectButton = styled(Button)`
  background-image: url(${reject});
`;

const ApplicantsPopup = ({ project, handleClose }: ApplicantsPopupProps) => {
  const { t } = useTranslation("project");
  const dispatch = useAppDispatch();

  const acceptApplicant = async (roleId: string, applicant: any) => {
    try {
      const data = await patch(
        `/projects/${project?._id}/roles/${roleId}/user/${applicant._id}`
      );
      if (data._id) {
        dispatch(assignUser({ roleId, applicant }));
      }
    } catch (e: any) {
      console.log(e);
    }
  };

  const rejectApplicant = async (roleId: string, applicantId: string) => {
    try {
      const data = await patch(
        `/projects/roles/${roleId}/application/${applicantId}/remove`
      );
      if (data._id) {
        dispatch(removeApplicant({ roleId, applicantId }));
      }
    } catch (e: any) {
      console.log(e);
    }
  };

  useEffect(() => {
    const applicants = project?.roles?.filter(
      (role) => role.applicants.length > 0
    );
    if (applicants && applicants.length < 1) {
      handleClose(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.roles]);

  return (
    <Popup heading={t("applications")} handleClose={handleClose}>
      {project?.roles
        ?.filter((role) => role.applicants?.length > 0)
        .map((role) => (
          <Wrapper key={role._id}>
            <RoleName>{role.name}</RoleName>
            <ApplicantsWrapper>
              {role.applicants.map((applicant) => (
                <Applicant key={applicant._id}>
                  <ApplicantPicture />
                  {applicant.name}
                  <RejectButton
                    onClick={() => rejectApplicant(role._id, applicant._id)}
                  />
                  <AcceptButton
                    onClick={() => acceptApplicant(role._id, applicant)}
                  />
                </Applicant>
              ))}
            </ApplicantsWrapper>
          </Wrapper>
        ))}
    </Popup>
  );
};

export default ApplicantsPopup;
